import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styles from './Expenses.module.css';
import './InvoiceScanner.css'; // Import scanner styles
import InvoiceScanner from './InvoiceScanner'; // Import the scanner component
import ExpenseExcelUpload from './ExpenseExcelUpload'; // Import the Excel upload component
import ExpensesModal from './ExpensesModal'; // Import the modal component (for column settings, filters)
import ExpenseForm from './ExpenseForm'; // Import the new expense form component
import RecurringExpensesPopup from './RecurringExpensesPopup';
import { useRecurringExpenses } from '../../hooks/useRecurringExpenses';
import {
  Calculator,
  Search,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash,
  Plus,
  DollarSign,
  FileText,
  TrendingUp,
  Filter,
  Camera,
  Upload,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet, // Added for Excel upload button
  CreditCard ,
   Calendar,// Added for payment method icon
   MoreVertical,
   Settings,
   Eye,
   EyeOff,
   Columns,
   RotateCcw,
   RotateCw,
   X,
   ArrowUpDown,
   Check
} from 'lucide-react';
import { addExpenses, updateExpenses, deleteExpenses, getExpenses } from '../../Fetch/fetchExpenses';
import { deleteImage, getImage } from '../../Fetch/fetchUploadImage';

// Action Menu Modal Component (replaces dropdown)
const ActionMenu = ({ expense, onEdit, onDelete, onViewInvoice, loading }) => {
  const [isOpen, setIsOpen] = useState(false);

  const actions = [];

  // Add view invoice action if invoice exists
  if (expense.invoiceImage || expense.receiptImageURL) {
    actions.push({
      label: '📄 הצג חשבונית',
      onClick: () => onViewInvoice(expense.invoiceImage || expense.receiptImageURL),
      disabled: loading
    });
  }

  actions.push({
    label: '✏️ ערוך',
    onClick: () => onEdit(expense.expenseID),
    disabled: loading
  });

  // Add delete action
  actions.push({
    label: '🗑️ מחק',
    onClick: () => onDelete(expense.expenseID),
    disabled: loading,
    isDanger: true
  });

  const handleActionClick = (action) => {
    if (!action.disabled) {
      action.onClick();
      setIsOpen(false);
    }
  };

  return (
    <>
      <button
        className={`${styles.actionMenuTrigger} ${loading ? styles.disabled : ''}`}
        onClick={() => setIsOpen(true)}
        disabled={loading}
        aria-label="פתח תפריט פעולות"
      >
        <span>⋯</span>
      </button>

      {isOpen && createPortal(
        <div className={styles.modalOverlay} onClick={() => setIsOpen(false)}>
          <div className={styles.actionMenuModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.actionMenuHeader}>
              <h3>פעולות</h3>
              <button
                className={styles.closeButton}
                onClick={() => setIsOpen(false)}
                aria-label="סגור"
              >
                ✕
              </button>
            </div>
            <div className={styles.actionMenuContent}>
              {actions.map((action, index) => (
                <button
                  key={index}
                  className={`${styles.actionMenuItem} ${action.isDanger ? styles.dangerAction : ''} ${action.disabled ? styles.disabled : ''}`}
                  onClick={() => handleActionClick(action)}
                  disabled={action.disabled}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// Column Settings Modal Component
const ColumnSettingsModal = ({ isOpen, onClose, visibleColumns, toggleColumn, resetColumns, isDragEnabled, toggleDragEnabled }) => {
  if (!isOpen) return null;

  const columnOptions = [
    { key: 'date', label: 'תאריך', icon: Calendar },
    { key: 'description', label: 'תיאור', icon: FileText },
    { key: 'category', label: 'קטגוריה', icon: DollarSign },
    { key: 'expenseType', label: 'סוג הוצאה', icon: TrendingUp },
    { key: 'paymentMethod', label: 'אמצעי תשלום', icon: CreditCard },
    { key: 'vendor', label: 'ספק', icon: Calculator },
    { key: 'amount', label: 'סכום', icon: DollarSign },
    { key: 'invoice', label: 'חשבונית', icon: FileText },
    { key: 'actions', label: 'פעולות', icon: Settings }
  ];

  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modalContent} ${styles.columnSettingsModal}`} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>
            <Settings size={20} />
            הגדרות עמודות
          </h3>
          <p>בחר אילו עמודות להציג בטבלה</p>
          <button
            onClick={onClose}
            className={styles.userModalCloseButton}
            aria-label="סגור"
          >
            <X size={20} />
          </button>
        </div>

        <div className={styles.dragToggleContainer}>
          <label className={styles.dragToggleLabel}>
            <input
              type="checkbox"
              checked={isDragEnabled}
              onChange={toggleDragEnabled}
              className={styles.columnCheckbox}
            />
            <div className={styles.dragToggleContent}>
              <ArrowUpDown size={16} />
              <span>{isDragEnabled ? 'מבוטל' : 'שינוי עמודות'}</span>
              <span className={styles.dragToggleHint}>
                {isDragEnabled ? 'מאופשר - גרור עמודות לשינוי סדר' : 'מבוטל'}
              </span>
            </div>
          </label>
        </div>

        <div className={styles.columnOptionsContainer}>
          {columnOptions.map(({ key, label, icon: Icon }) => (
            <div key={key} className={styles.columnOption}>
              <label className={styles.columnLabel}>
                <input
                  type="checkbox"
                  checked={visibleColumns[key]}
                  onChange={() => toggleColumn(key)}
                  className={styles.columnCheckbox}
                />
                <div className={styles.columnOptionContent}>
                  <Icon size={16} />
                  <span>{label}</span>
                  {visibleColumns[key] ? (
                    <Eye size={14} className={styles.visibilityIcon} />
                  ) : (
                    <EyeOff size={14} className={styles.visibilityIcon} />
                  )}
                </div>
              </label>
            </div>
          ))}
        </div>

        <div className={styles.modalActions}>
          <button
            onClick={resetColumns}
            className={styles.resetButton}
            title="איפוס להגדרות ברירת המחדל"
          >
            <RotateCcw size={16} />
            איפוס
          </button>
          <button
            onClick={onClose}
            className={styles.confirmButton}
          >
            <Settings size={16} />
            סגור
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Sort Menu Component
const SortMenu = ({ isOpen, columnKey, columnLabel, position, onClose, onSort, currentSort }) => {
  if (!isOpen) return null;

  const isCurrentColumn = currentSort.field === columnKey;
  const currentDirection = isCurrentColumn ? currentSort.order : null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999
        }}
        onClick={onClose}
      />
      {/* Menu */}
      <div
        className={styles.sortMenu}
        style={{
          position: 'fixed',
          top: `${position.y}px`,
          left: `${position.x}px`,
          zIndex: 1000
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.sortMenuHeader}>
          <ArrowUpDown size={16} />
          <span>מיון: {columnLabel}</span>
          <button onClick={onClose} className={styles.sortMenuClose}>
            <X size={16} />
          </button>
        </div>
        <div className={styles.sortMenuOptions}>
          <button
            onClick={() => {
              onSort(columnKey, 'asc');
              onClose();
            }}
            className={`${styles.sortMenuOption} ${isCurrentColumn && currentDirection === 'asc' ? styles.activeSortOption : ''}`}
          >
            <ChevronUp size={16} />
            מיון עולה (א-ת)
            {isCurrentColumn && currentDirection === 'asc' && <span className={styles.checkMark}>✓</span>}
          </button>
          <button
            onClick={() => {
              onSort(columnKey, 'desc');
              onClose();
            }}
            className={`${styles.sortMenuOption} ${isCurrentColumn && currentDirection === 'desc' ? styles.activeSortOption : ''}`}
          >
            <ChevronDown size={16} />
            מיון יורד (ת-א)
            {isCurrentColumn && currentDirection === 'desc' && <span className={styles.checkMark}>✓</span>}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

// Mobile ExpenseCard Component
const MobileExpenseCard = ({ expense, onEdit, onDelete, onViewInvoice }) => {
  const [showMenu, setShowMenu] = useState(false);

  const handleEdit = () => {
    setShowMenu(false);
    onEdit(expense);
  };

  const handleDelete = () => {
    setShowMenu(false);
    onDelete(expense);
  };

  const handleViewInvoice = () => {
    setShowMenu(false);
    onViewInvoice(expense.invoiceImage || expense.receiptImageURL);
  };

  return (
    <div className={styles.mobileExpenseCard}>
      {/* Menu Button - Top Right */}
      <div className={styles.menuContainer}>
        <button className={styles.menuBtn} onClick={() => setShowMenu(!showMenu)}>
          <MoreVertical size={20} />
        </button>
        {showMenu && (
          <>
            <div className={styles.menuOverlay} onClick={() => setShowMenu(false)} />
            <div className={styles.menuDropdown}>
              <button className={styles.menuItem} onClick={handleEdit}>
                <Edit size={16} />
                <span>ערוך</span>
              </button>
              {(expense.invoiceImage || expense.receiptImageURL) && (
                <button className={styles.menuItem} onClick={handleViewInvoice}>
                  <FileText size={16} />
                  <span>חשבונית</span>
                </button>
              )}
              <button className={`${styles.menuItem} ${styles.menuItemDanger}`} onClick={handleDelete}>
                <Trash size={16} />
                <span>מחק</span>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Avatar - Top Left with Receipt icon */}
      <div className={styles.expenseAvatar}>
        <FileText size={36} className={styles.avatarIcon} />
      </div>

      {/* Expense Info */}
      <div className={styles.expenseInfo}>
        <h2 className={styles.expenseName}>{expense.description}</h2>
        <p className={styles.expenseCategory}>{expense.category || 'ללא קטגוריה'}</p>
        <p className={styles.expenseVendor}>{expense.vendor || '-'}</p>
      </div>

      {/* Stats */}
      <div className={styles.expenseStats}>
        <div className={styles.statItem}>
          <div className={styles.statLabel}>סכום</div>
          <div className={styles.statValue}>{parseFloat(expense.amount || 0).toLocaleString()} ₪</div>
        </div>
        <div className={styles.statDivider}></div>
        <div className={styles.statItem}>
          <div className={styles.statLabel}>תאריך</div>
          <div className={styles.statValue}>{expense.date ? new Date(expense.date).toLocaleDateString('he-IL') : '-'}</div>
        </div>
      </div>
    </div>
  );
};

const Expenses = () => {
  // State variables
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buildingId, setBuildingId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('expensesTable_visibleColumns');
    return saved ? JSON.parse(saved) : {
      date: true,
      description: true,
      category: true,
      expenseType: true,
      paymentMethod: true,
      vendor: true,
      amount: true,
      invoice: true,
      actions: true
    };
  });

  // Column order state
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem('expensesTable_columnOrder');
    return saved ? JSON.parse(saved) : ['date', 'description', 'category', 'expenseType', 'paymentMethod', 'vendor', 'amount', 'invoice', 'actions'];
  });

  // Drag and drop enabled state
  const [isDragEnabled, setIsDragEnabled] = useState(() => {
    const saved = localStorage.getItem('expensesTable_dragEnabled');
    return saved ? JSON.parse(saved) : false;
  });

  // Sort menu state
  const [sortMenuState, setSortMenuState] = useState({
    isOpen: false,
    columnKey: null,
    columnLabel: null,
    position: { x: 0, y: 0 }
  });

  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [expensesPerPage, setExpensesPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [showMobileSearch, setShowMobileSearch] = useState(false); // Mobile search modal
  const [editingExpense, setEditingExpense] = useState(null); // Changed from editingId
  const [newExpense, setNewExpense] = useState({
    date: '',
    description: '',
    amount: '',
    category: 'תחזוקה שוטפת',
    expenseType: 'משתנה',
    paymentMethod: 'מזומן', // Added payment method
    vendor: '',
    invoiceNumber: '',
    invoiceImage: '',
    paidTo: '',
    receiptImageURL: '',
    uploadedFile: null
  });
  const [showForm, setShowForm] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showExpenseUpload, setShowExpenseUpload] = useState(false); // New state for expense upload modal
  const [isExporting, setIsExporting] = useState(false);
  const [selectedInvoiceImage, setSelectedInvoiceImage] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [sortOrder, setSortOrder] = useState('date-desc');
  const [sortField, setSortField] = useState('date');
  const [showFilters, setShowFilters] = useState(false);

  // Check payment state
  const [checks, setChecks] = useState([{ checkNumber: '', checkMaturityDate: '', amount: '' }]);

  // Validation state
  const [validationErrors, setValidationErrors] = useState({});

  // File upload related states
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Recurring expenses hook
  const {
    recurringExpenses,
    isLoading: isRecurringLoading,
    error: recurringError,
    showPopup: showRecurringPopup,
    closePopup: closeRecurringPopup,
    handleExpensesCreated: handleRecurringExpensesCreated,
    refreshRecurringExpenses,
    showPopupManually
  } = useRecurringExpenses(buildingId, false); // Set to false initially since we want manual control

  const getAvailableYears = React.useMemo(() => {
    const years = Array.isArray(expenses)
      ? expenses
        .map(expense => {
          const date = new Date(expense.date);
          return date.getFullYear();
        })
        .filter((year, index, arr) => arr.indexOf(year) === index)
        .sort((a, b) => b - a) // Sort descending (newest first)
      : [];
    return years;
  }, [expenses]);

  const sortExpenses = (expenses, field, order) => {
    return [...expenses].sort((a, b) => {
      let valueA, valueB;

      switch (field) {
        case 'date':
          valueA = new Date(a.date);
          valueB = new Date(b.date);
          break;
        case 'amount':
          valueA = parseFloat(a.amount) || 0;
          valueB = parseFloat(b.amount) || 0;
          break;
        case 'description':
          valueA = (a.description || '').toLowerCase();
          valueB = (b.description || '').toLowerCase();
          break;
        case 'category':
          valueA = (a.category || '').toLowerCase();
          valueB = (b.category || '').toLowerCase();
          break;
        default:
          valueA = a[field] || '';
          valueB = b[field] || '';
      }

      if (order === 'asc') {
        if (valueA < valueB) return -1;
        if (valueA > valueB) return 1;
        return 0;
      } else {
        if (valueA > valueB) return -1;
        if (valueA < valueB) return 1;
        return 0;
      }
    });
  };

  // Categories for dropdown
  const categories = [
    'תחזוקה שוטפת',
    'תיקונים',
    'חשבונות',
    'ביטוח',
    'שיפוצים',
    'אחר'
  ];

  // Expense types for dropdown
  const expenseTypes = [
    'קבועה',
    'משתנה'
  ];

  // Payment methods for dropdown - NEW
  const paymentMethods = [
    'מזומן',
    'צ\'ק',
    'העברה בנקאית',
    'ביט'
  ];

  // Column visibility functions
  const toggleColumn = (columnKey) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey]
    };
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('expensesTable_visibleColumns', JSON.stringify(newVisibleColumns));
  };

  const resetColumns = () => {
    const defaultColumns = {
      date: true,
      description: true,
      category: true,
      expenseType: true,
      paymentMethod: true,
      vendor: true,
      amount: true,
      invoice: true,
      actions: true
    };
    const defaultOrder = ['date', 'description', 'category', 'expenseType', 'paymentMethod', 'vendor', 'amount', 'invoice', 'actions'];

    setVisibleColumns(defaultColumns);
    setColumnOrder(defaultOrder);
    localStorage.setItem('expensesTable_visibleColumns', JSON.stringify(defaultColumns));
    localStorage.setItem('expensesTable_columnOrder', JSON.stringify(defaultOrder));
  };

  const toggleDragEnabled = () => {
    const newValue = !isDragEnabled;
    setIsDragEnabled(newValue);
    localStorage.setItem('expensesTable_dragEnabled', JSON.stringify(newValue));
  };

  // Drag and drop handlers
  const handleDragStart = (e, columnKey) => {
    if (!isDragEnabled) return;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnKey);
  };

  const handleDragOver = (e) => {
    if (!isDragEnabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetColumnKey) => {
    if (!isDragEnabled) return;
    e.preventDefault();

    const sourceColumnKey = e.dataTransfer.getData('text/plain');
    if (sourceColumnKey === targetColumnKey) return;

    const newOrder = [...columnOrder];
    const sourceIndex = newOrder.indexOf(sourceColumnKey);
    const targetIndex = newOrder.indexOf(targetColumnKey);

    // Remove source and insert at target position
    newOrder.splice(sourceIndex, 1);
    newOrder.splice(targetIndex, 0, sourceColumnKey);

    setColumnOrder(newOrder);
    localStorage.setItem('expensesTable_columnOrder', JSON.stringify(newOrder));
  };

  // Statistics calculations
  const totalExpenses = Array.isArray(filteredExpenses) ? filteredExpenses.length : 0;
  const totalAmount = Array.isArray(filteredExpenses)
    ? filteredExpenses.reduce((sum, expense) => sum + (parseFloat(expense.amount) || 0), 0)
    : 0;
  const thisMonthExpenses = Array.isArray(filteredExpenses)
    ? filteredExpenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      const now = new Date();
      return expenseDate.getMonth() === now.getMonth() &&
        expenseDate.getFullYear() === now.getFullYear();
    }).length
    : 0;

  // Get unique categories for dropdown filter
  const uniqueCategories = React.useMemo(() => {
    const cats = Array.isArray(expenses)
      ? expenses
        .map(e => e.category)
        .filter(cat => cat && cat !== '')
        .filter((cat, index, arr) => arr.indexOf(cat) === index)
        .sort()
      : [];
    return cats;
  }, [expenses]);

  // Mobile Expense Card Component
  const ExpenseCard = ({ expense }) => (
    <div className={styles.dataCard}>
      <div className={styles.cardHeader}>
        <div className={styles.cardTitle}>
          <Calculator size={16} className={styles.cardIcon} />
          <span>{expense.description}</span>
        </div>
        <span className={styles.cardAmount}>
          {parseFloat(expense.amount || 0).toLocaleString()} ₪
        </span>
      </div>
      <div className={styles.cardBody}>
        <div className={styles.cardInfo}>
          <FileText size={14} />
          <span>{expense.date ? new Date(expense.date).toLocaleDateString('he-IL') : '-'}</span>
        </div>
        <div className={styles.cardInfo}>
          <TrendingUp size={14} />
          <span>{expense.category}</span>
        </div>

        {/* Expense Type */}
        {expense.expenseType && (
          <div className={styles.cardInfo}>
            <DollarSign size={14} />
            <span>סוג: {expense.expenseType}</span>
          </div>
        )}

        {/* Payment Method */}
        {expense.paymentMethod && (
          <div className={styles.cardInfo}>
            <CreditCard size={14} />
            <span>תשלום: {expense.paymentMethod}</span>
          </div>
        )}

        {/* Vendor */}
        {expense.vendor && (
          <div className={styles.cardInfo}>
            <DollarSign size={14} />
            <span>ספק: {expense.vendor}</span>
          </div>
        )}

        {/* Paid To */}
        {expense.paidTo && (
          <div className={styles.cardInfo}>
            <DollarSign size={14} />
            <span>תשלום ל-: {expense.paidTo}</span>
          </div>
        )}

        {/* Invoice Number */}
        {expense.invoiceNumber && (
          <div className={styles.cardInfo}>
            <FileText size={14} />
            <span>חשבונית: #{expense.invoiceNumber}</span>
          </div>
        )}

        <div className={styles.cardActions}>
          <button
            className={styles.editButton}
            onClick={() => handleEditMode(expense.expenseID)}
            title="עריכה"
          >
            <Edit size={16} />
          </button>
          <button
            className={styles.deleteButton}
            onClick={() => handleDeleteExpense(expense.expenseID)}
            title="מחיקה"
          >
            <Trash size={16} />
          </button>
          {(expense.invoiceImage || expense.receiptImageURL) && (
            <button
              className={styles.viewButton}
              onClick={() => viewInvoiceImage(expense.invoiceImage || expense.receiptImageURL)}
              title="הצג חשבונית"
            >
              <FileText size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Mobile detection effect
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Add event listener for building changes
  useEffect(() => {
    const handleBuildingChange = () => {
      fetchExpenses();
    };

    window.addEventListener('buildingChanged', handleBuildingChange);
    window.addEventListener('userDataChanged', handleBuildingChange);

    return () => {
      window.removeEventListener('buildingChanged', handleBuildingChange);
      window.removeEventListener('userDataChanged', handleBuildingChange);
    };
  }, []);

  const handleDropdownToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const actionButtonGroup = document.querySelector(`.${styles.actionButtonGroup}`);
      if (actionButtonGroup && !actionButtonGroup.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      // Add a small delay to prevent immediate closing
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('touch', handleClickOutside);
      }, 100);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touch', handleClickOutside);
    };
  }, [showDropdown, styles.actionButtonGroup]);

  // ESC KEY HANDLING
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    if (showForm || editingExpense) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [showForm, editingExpense]);

  // MODAL KEYBOARD HANDLING
  useEffect(() => {
    const handleIOSKeyboard = () => {
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        const modalBody = document.querySelector(`.${styles.modalBody}`);
        const inputs = document.querySelectorAll(`.${styles.formGroup} input, .${styles.formGroup} select`);

        inputs.forEach(input => {
          input.addEventListener('focus', () => {
            setTimeout(() => {
              if (modalBody) {
                input.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                  inline: 'nearest'
                });
              }
            }, 300);
          });

          input.addEventListener('blur', () => {
            setTimeout(() => {
              window.scrollTo(0, 0);
            }, 300);
          });
        });
      }
    };

    if (showForm || editingExpense) {
      handleIOSKeyboard();
    }

    return () => {
      const inputs = document.querySelectorAll(`.${styles.formGroup} input, .${styles.formGroup} select`);
      inputs.forEach(input => {
        input.removeEventListener('focus', handleIOSKeyboard);
        input.removeEventListener('blur', handleIOSKeyboard);
      });
    };
  }, [showForm, editingExpense, styles.modalBody, styles.formGroup]);

  // Get buildingId from localStorage
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    const buildingID = userData?.buildingId;

    if (buildingID !== null && buildingID !== undefined) {
      setBuildingId(buildingID);
    } else {
      setError('לא נמצא מזהה בניין. אנא התחבר מחדש.');
      setLoading(false);
    }
  }, []);

  // Filter expenses based on search, category selection, and tab
  useEffect(() => {
    let filtered = Array.isArray(expenses) ? [...expenses] : [];

    // Filter by tab
    if (activeTab !== 'all') {
      filtered = filtered.filter(expense => expense.category === activeTab);
    }

    // Filter by category selection
    if (selectedCategory) {
      filtered = filtered.filter(expense => expense.category === selectedCategory);
    }

    // Filter by month and year - NEW FILTER
    if (selectedMonth || selectedYear) {
      filtered = filtered.filter(expense => {
        if (!expense.date) return false;

        const expenseDate = new Date(expense.date);
        const expenseMonth = expenseDate.getMonth() + 1; // getMonth() returns 0-11, we want 1-12
        const expenseYear = expenseDate.getFullYear();

        let matchesMonth = true;
        let matchesYear = true;

        if (selectedMonth) {
          matchesMonth = expenseMonth === parseInt(selectedMonth);
        }

        if (selectedYear) {
          matchesYear = expenseYear === parseInt(selectedYear);
        }

        return matchesMonth && matchesYear;
      });
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(expense => {
        const searchLower = searchTerm.toLowerCase();
        return (
          expense.description?.toLowerCase().includes(searchLower) ||
          expense.category?.toLowerCase().includes(searchLower) ||
          expense.expenseType?.toLowerCase().includes(searchLower) ||
          expense.paymentMethod?.toLowerCase().includes(searchLower) ||
          expense.amount?.toString().includes(searchTerm) ||
          expense.date?.includes(searchTerm) ||
          (expense.vendor && expense.vendor.toLowerCase().includes(searchLower)) ||
          (expense.invoiceNumber && expense.invoiceNumber.toLowerCase().includes(searchLower)) ||
          (expense.paidTo && expense.paidTo.toLowerCase().includes(searchLower))
        );
      });
    }

    // Apply sorting
    const [field, order] = sortOrder.split('-');
    filtered = sortExpenses(filtered, field, order);

    setFilteredExpenses(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedMonth, selectedYear, expenses, activeTab, sortOrder]);

  const handleSort = (field, order) => {
    setSortField(field);
    setSortOrder(`${field}-${order}`);
  };

  const handleColumnHeaderClick = (e, columnKey, columnLabel, isSortable) => {
    if (!isSortable || isDragEnabled) return;

    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setSortMenuState({
      isOpen: true,
      columnKey: columnKey,
      columnLabel: columnLabel,
      position: {
        x: rect.left,
        y: rect.bottom
      }
    });
  };

  const closeSortMenu = () => {
    setSortMenuState({
      isOpen: false,
      columnKey: null,
      columnLabel: null,
      position: { x: 0, y: 0 }
    });
  };

  // Clear filters function
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedMonth('');
    setSelectedYear('');
  };

  // Show success animation function
  const showSuccessMessage = () => {
    setShowSuccessAnimation(true);
    setTimeout(() => {
      setShowSuccessAnimation(false);
    }, 2000);
  };

  // Close modal function
  const closeModal = () => {
    setShowForm(false);
    setEditingExpense(null);
    setNewExpense({
      date: '',
      description: '',
      amount: '',
      category: 'תחזוקה שוטפת',
      expenseType: 'משתנה',
      paymentMethod: 'מזומן', // Reset payment method
      vendor: '',
      invoiceNumber: '',
      invoiceImage: '',
      paidTo: '',
      receiptImageURL: '',
      uploadedFile: null
    });
    // Reset checks array
    setChecks([{ checkNumber: '', checkMaturityDate: '', amount: '' }]);
    // Clear validation errors
    setValidationErrors({});
    // Clear file inputs
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  // handleBackdropClick function removed - now handled by ExpensesModal component

  // Pagination
  const indexOfLastExpense = currentPage * expensesPerPage;
  const indexOfFirstExpense = indexOfLastExpense - expensesPerPage;
  const currentExpenses = filteredExpenses.slice(indexOfFirstExpense, indexOfLastExpense);
  const totalPages = Math.ceil(filteredExpenses.length / expensesPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Fetch expenses from database
  const fetchExpenses = async () => {
    if (!buildingId) {
      const userData = JSON.parse(localStorage.getItem('user'));
      const currentBuildingId = userData?.buildingId;

      if (!currentBuildingId) {
        return;
      }

      setBuildingId(currentBuildingId);
    }

    const activeBuildingId = buildingId || JSON.parse(localStorage.getItem('user'))?.buildingId;

    try {
      setLoading(true);
      setError(null);


      const response = await getExpenses(activeBuildingId);
      let expensesData = response?.data || [];

      // Client-side sorting as backup (newest first)
      expensesData = expensesData.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);

        // Primary sort: by date (newest first)
        if (dateB.getTime() !== dateA.getTime()) {
          return dateB.getTime() - dateA.getTime();
        }

        // Secondary sort: by expenseID (newest first)
        return (b.expenseID || 0) - (a.expenseID || 0);
      });

      if (expensesData.length > 0) {
      }

      setExpenses(expensesData);
    } catch (err) {
      setError('שגיאה בטעינת ההוצאות: ' + err.message);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  // Load expenses when buildingId changes
  useEffect(() => {
    fetchExpenses();
  }, [buildingId]);

  // Excel Export Function
  const exportToExcel = async (exportAllPages = false) => {
    try {
      setIsExporting(true);

      // Dynamic import of SheetJS
      const XLSX = await import('xlsx');

      // Determine which data to export
      const dataToExport = exportAllPages ? filteredExpenses : currentExpenses;

      if (dataToExport.length === 0) {
        alert('אין נתונים לייצוא');
        return;
      }

      // Prepare data for Excel
      const excelData = dataToExport.map((expense, index) => ({
        'מספר רשומה': index + 1,
        'תאריך': new Date(expense.date).toLocaleDateString('he-IL'),
        'קטגוריה': expense.category || '',
        'תיאור': expense.description || '',
        'סכום': expense.amount || 0,
        'אמצעי תשלום': expense.paymentMethod || '',
        'מספר חשבונית': expense.invoiceNumber || '',
        'שולם ל': expense.paidTo || '',
        'חודש': expense.month || '',
        'שנה': expense.year || '',
        'יש קבלה': expense.receiptImageURL ? 'כן' : 'לא'
      }));

      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 8 },  // מספר רשומה
        { wch: 12 }, // תאריך
        { wch: 15 }, // קטגוריה
        { wch: 30 }, // תיאור
        { wch: 12 }, // סכום
        { wch: 15 }, // אמצעי תשלום
        { wch: 15 }, // מספר חשבונית
        { wch: 20 }, // שולם ל
        { wch: 8 },  // חודש
        { wch: 8 },  // שנה
        { wch: 10 }  // יש קבלה
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      const currentDate = new Date().toLocaleDateString('he-IL');
      const sheetName = 'הוצאות';

      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      // Generate filename
      const filename = exportAllPages
        ? `הוצאות_כל_הנתונים_${currentDate.replace(/\//g, '-')}.xlsx`
        : `הוצאות_עמוד_${currentPage}_${currentDate.replace(/\//g, '-')}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      alert(exportAllPages 
        ? `יוצא בהצלחה! ${dataToExport.length} הוצאות`
        : `יוצא בהצלחה! ${dataToExport.length} הוצאות (עמוד ${currentPage})`
      );

    } catch (error) {
      alert('שגיאה בייצוא הקובץ: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  // File upload handlers and other existing functions...
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const userData = JSON.parse(localStorage.getItem('user'));
    const currentBuildingId = buildingId ?? userData?.buildingId;

    if (currentBuildingId === null || currentBuildingId === undefined) {
      alert('שגיאה: לא נמצא מזהה בניין. אנא התחבר מחדש.');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('סוג קובץ לא נתמך. אנא בחר תמונה או קובץ PDF.');
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('הקובץ גדול מדי. גודל מקסימלי: 10MB');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('buildingId', currentBuildingId.toString());

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://localhost:7204'}/api/UploadImage/AddImage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} - ${errorText}`);
      }

      const uploadResult = await response.json();

      if (uploadResult.success) {
        const base64 = await convertFileToBase64(file);

        const setExpenseData = editingExpense ? setEditingExpense : setNewExpense;
        setExpenseData(prev => ({
          ...prev,
          receiptImageURL: uploadResult.data.filePath,
          invoiceImage: base64,
          uploadedFile: {
            name: uploadResult.data.fileName,
            size: file.size,
            type: file.type,
            base64: base64,
            path: uploadResult.data.filePath,
            serverData: uploadResult.data
          }
        }));

        alert('קובץ הועלה בהצלחה לשרת!');
      } else {
        throw new Error(uploadResult.message || 'Upload failed');
      }

    } catch (error) {
      if (error.message.includes('401')) {
        alert('שגיאה באימות. אנא התחבר מחדש.');
      } else {
        alert('שגיאה בהעלאת הקובץ: ' + error.message);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleCameraCapture = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const userData = JSON.parse(localStorage.getItem('user'));
    const currentBuildingId = buildingId ?? userData?.buildingId;

    if (currentBuildingId === null || currentBuildingId === undefined) {
      alert('שגיאה: לא נמצא מזהה בניין. אנא התחבר מחדש.');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('buildingId', currentBuildingId.toString());

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://localhost:7204'}/api/UploadImage/AddImage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} - ${errorText}`);
      }

      const uploadResult = await response.json();

      if (uploadResult.success) {
        const base64 = await convertFileToBase64(file);

        const setExpenseData = editingExpense ? setEditingExpense : setNewExpense;
        setExpenseData(prev => ({
          ...prev,
          receiptImageURL: uploadResult.data.filePath,
          invoiceImage: base64,
          uploadedFile: {
            name: uploadResult.data.fileName,
            size: file.size,
            type: file.type,
            base64: base64,
            path: uploadResult.data.filePath,
            serverData: uploadResult.data
          }
        }));

        alert('תמונה נצולמה והועלתה בהצלחה לשרת!');
      } else {
        throw new Error(uploadResult.message || 'Upload failed');
      }

    } catch (error) {
      if (error.message.includes('401')) {
        alert('שגיאה באימות. אנא התחבר מחדש.');
      } else {
        alert('שגיאה בעיבוד התמונה: ' + error.message);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const removeUploadedFile = async () => {
    const expenseData = editingExpense || newExpense;
    const setExpenseData = editingExpense ? setEditingExpense : setNewExpense;

    if (expenseData.uploadedFile && expenseData.receiptImageURL) {
      try {
        await deleteImage(expenseData.receiptImageURL, buildingId);
      } catch (error) {
      }
    }

    setExpenseData(prev => ({
      ...prev,
      receiptImageURL: '',
      invoiceImage: '',
      uploadedFile: null
    }));

    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const viewInvoiceImage = async (imageData) => {
    try {
      if (imageData.startsWith('data:')) {
        setSelectedInvoiceImage(imageData);
      } else {
        const response = await getImage(imageData);

        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          setSelectedInvoiceImage(blobUrl);
        } else {
          throw new Error('Failed to load image from server');
        }
      }
    } catch (error) {
      alert('שגיאה בטעינת התמונה: ' + error.message);
    }
  };

  const handleAddExpense = async () => {
    // Clear previous validation errors
    setValidationErrors({});

    // Validate main fields
    const errors = {};
    if (!newExpense.date) {
      errors.date = 'שדה חובה';
    }
    if (!newExpense.description) {
      errors.description = 'שדה חובה';
    }
    if (!newExpense.amount || parseFloat(newExpense.amount) <= 0) {
      errors.amount = 'נדרש סכום תקין';
    }

    // Validate check fields if payment method is צ'ק
    if (newExpense.paymentMethod === "צ'ק") {
      const checkErrors = {};
      checks.forEach((check, index) => {
        const checkFieldErrors = {};
        if (!check.checkNumber.trim()) {
          checkFieldErrors.checkNumber = 'נדרש מספר צ\'ק';
        }
        if (!check.checkMaturityDate) {
          checkFieldErrors.checkMaturityDate = 'נדרש תאריך פירעון';
        }
        if (!check.amount || parseFloat(check.amount) <= 0) {
          checkFieldErrors.amount = 'נדרש סכום תקין';
        }
        if (Object.keys(checkFieldErrors).length > 0) {
          checkErrors[`check_${index}`] = checkFieldErrors;
        }
      });
      if (Object.keys(checkErrors).length > 0) {
        errors.checks = checkErrors;
      }
    }

    // If there are validation errors, set them and return
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    const activeBuildingId = buildingId || JSON.parse(localStorage.getItem('user'))?.buildingId;

    if (!activeBuildingId) {
      alert('שגיאה: לא נמצא מזהה בניין');
      return;
    }

    try {
      setLoading(true);
      setError("");

      // If payment method is צ'ק, create multiple expenses (one for each check)
      if (newExpense.paymentMethod === "צ'ק" && checks.length > 0) {
        const checkExpenses = checks.map(check => ({
          BuildingID: activeBuildingId,
          Date: newExpense.date ? new Date(newExpense.date).toISOString() : null,
          Description: newExpense.description,
          Amount: parseFloat(newExpense.amount), // Keep original amount as fallback
          Category: newExpense.category,
          ExpenseType: newExpense.expenseType,
          PaymentMethod: newExpense.paymentMethod,
          PaidTo: newExpense.paidTo,
          Vendor: newExpense.vendor,
          InvoiceNumber: newExpense.invoiceNumber,
          ReceiptImageURL: newExpense.receiptImageURL,
          SupplierID: null,
          InvoiceImage: newExpense.invoiceImage,
          CheckNumber: check.checkNumber,
          CheckMaturityDate: check.checkMaturityDate ? new Date(check.checkMaturityDate).toISOString() : null,
          CheckAmount: check.amount ? parseFloat(check.amount) : null
        }));

        // Call the new endpoint for multiple check expenses
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://localhost:7204'}/api/Expenses/AddMultipleCheckExpenses`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(checkExpenses)
        });

        if (!response.ok) {
          throw new Error('Failed to add check expenses');
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.message || 'Failed to add check expenses');
        }
      } else {
        // Regular expense (not check)
        const expenseData = {
          BuildingID: activeBuildingId,
          Date: newExpense.date ? new Date(newExpense.date).toISOString() : null,
          Description: newExpense.description,
          Amount: parseFloat(newExpense.amount),
          Category: newExpense.category,
          ExpenseType: newExpense.expenseType,
          PaymentMethod: newExpense.paymentMethod,
          PaidTo: newExpense.paidTo,
          Vendor: newExpense.vendor,
          InvoiceNumber: newExpense.invoiceNumber,
          ReceiptImageURL: newExpense.receiptImageURL,
          SupplierID: null,
          InvoiceImage: newExpense.invoiceImage,
          StopPermanentPayment: false
        };

        // Only add CheckNumber if payment method is צ'ק
        if (newExpense.paymentMethod === "צ'ק") {
          expenseData.CheckNumber = checks[0]?.checkNumber || null;
        }

        await addExpenses(expenseData);
      }

      closeModal();
      await fetchExpenses();
      showSuccessMessage();

    } catch (err) {
      alert('שגיאה בהוספת ההוצאה: ' + err.message);
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteExpense = async (id) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק הוצאה זו?')) {
      try {
        const expenseToDelete = expenses.find(expense => expense.expenseID === id);
        await deleteExpenses(id);

        if (expenseToDelete?.receiptImageURL) {
          try {
            await deleteImage(expenseToDelete.receiptImageURL, buildingId);
          } catch (fileError) {
          }
        }

        await fetchExpenses();
      } catch (err) {
        alert('שגיאה במחיקת ההוצאה: ' + err.message);
      }
    }
  };


  const handleEditMode = (id) => {
    const expenseToEdit = expenses.find(expense => expense.expenseID === id);

    if (!expenseToEdit) {
      alert('לא נמצאה הוצאה לעריכה');
      return;
    }

    // Map ALL fields properly, ensuring consistent field names
    const editData = {
      // Core fields
      expenseID: expenseToEdit.expenseID,
      buildingID: expenseToEdit.buildingID,
      date: expenseToEdit.date ? new Date(expenseToEdit.date).toISOString().split('T')[0] : '',
      description: expenseToEdit.description || '',
      amount: expenseToEdit.amount || '',
      category: expenseToEdit.category || 'תחזוקה שוטפת',

      // Additional fields with proper defaults
      expenseType: expenseToEdit.expenseType || 'קבועה',
      paymentMethod: expenseToEdit.paymentMethod || 'מזומן',
      paidTo: expenseToEdit.paidTo || '',
      vendor: expenseToEdit.vendor || '',
      invoiceNumber: expenseToEdit.invoiceNumber || '',

      // Image and file fields
      invoiceImage: expenseToEdit.invoiceImage || '',
      receiptImageURL: expenseToEdit.receiptImageURL || '',

      // Additional IDs
      supplierID: expenseToEdit.supplierID || null,

      // File upload state
      uploadedFile: null
    };


    setEditingExpense(editData);
    setShowForm(false);
    if (showScanner) setShowScanner(false);
  };


  const handleSaveEdit = async () => {
    if (!editingExpense.date || !editingExpense.description || !editingExpense.amount) {
      alert('נא למלא את כל השדות הנדרשים');
      return;
    }

    const expenseId = editingExpense.expenseID;

    if (!expenseId) {
      alert('שגיאה: לא נמצא מזהה הוצאה');
      return;
    }

    try {
      setLoading(true);
      setError("");

      // Map all fields correctly, ensuring ExpenseID is properly set
      const updatedExpense = {
        ExpenseID: expenseId,
        BuildingID: editingExpense.buildingID || buildingId || JSON.parse(localStorage.getItem('user'))?.buildingId,
        Date: editingExpense.date ? new Date(editingExpense.date).toISOString() : null,
        Description: editingExpense.description,
        Amount: parseFloat(editingExpense.amount),
        Category: editingExpense.category,
        ExpenseType: editingExpense.expenseType,
        PaymentMethod: editingExpense.paymentMethod,
        PaidTo: editingExpense.paidTo,
        Vendor: editingExpense.vendor,
        InvoiceNumber: editingExpense.invoiceNumber,
        ReceiptImageURL: editingExpense.receiptImageURL,
        SupplierID: editingExpense.supplierID || null,
        InvoiceImage: editingExpense.invoiceImage
      };


      await updateExpenses(updatedExpense);
      closeModal();
      await fetchExpenses();
      showSuccessMessage();

    } catch (err) {
      alert('שגיאה בעדכון ההוצאה: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExpenseInputChange = useCallback((e) => {
    const { name, value } = e.target;
    const setExpenseData = editingExpense ? setEditingExpense : setNewExpense;

    setExpenseData(prev => ({ ...prev, [name]: value }));

    // Clear validation error for this field when user types
    setValidationErrors(prev => {
      if (prev[name]) {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      }
      return prev;
    });
  }, [editingExpense]);

  const handlePaymentMethodChange = (e) => {
    const { name, value } = e.target;
    const setExpenseData = editingExpense ? setEditingExpense : setNewExpense;

    // Check if trying to select צ'ק without a date
    setExpenseData(prev => {
      if (value === 'צ\'ק' && !prev.date) {
        // Highlight the date field as invalid
        setValidationErrors(errors => ({
          ...errors,
          date: 'נדרש תאריך לפני בחירת צ\'ק'
        }));
        return prev;
      } else {
        // Clear date validation error if it exists
        setValidationErrors(errors => {
          const newErrors = { ...errors };
          delete newErrors.date;
          return newErrors;
        });
      }

      // Reset checks array when payment method changes away from צ'ק or set first check date from main date when switching to צ'ק
      if (value !== 'צ\'ק') {
        setChecks([{ checkNumber: '', checkMaturityDate: '', amount: '' }]);
      } else if (value === 'צ\'ק') {
        // When switching to צ'ק, use the main date for first check
        const mainDate = prev.date || '';
        const mainAmount = prev.amount || '';
        setChecks([{ checkNumber: '', checkMaturityDate: mainDate, amount: mainAmount }]);
      }

      return { ...prev, [name]: value };
    });
  };

  const handleScanComplete = (scannedData) => {
    setNewExpense({
      ...newExpense,
      date: scannedData.date || '',
      description: scannedData.description || '',
      amount: scannedData.amount || '',
      expenseType: scannedData.expenseType || 'קבועה',
      paymentMethod: scannedData.paymentMethod || 'מזומן', // Added payment method from scan
      vendor: scannedData.vendor || '',
      invoiceNumber: scannedData.invoiceNumber || '',
      invoiceImage: scannedData.invoiceImage || ''
    });

    setShowScanner(false);
    setShowForm(true);
  };

  const toggleScanner = () => {
    setShowScanner(!showScanner);
    if (showForm) setShowForm(false);
    if (editingExpense) setEditingExpense(null);
  };

  const closeInvoiceImageModal = () => {
    if (selectedInvoiceImage && selectedInvoiceImage.startsWith('blob:')) {
      URL.revokeObjectURL(selectedInvoiceImage);
    }
    setSelectedInvoiceImage(null);
  };

  const handleAddExpenseClick = () => {
    setShowForm(true);
    setShowDropdown(false);
    if (showScanner) setShowScanner(false);
    if (editingExpense) setEditingExpense(null);
  };

  const handleScannerClick = () => {
    setShowScanner(true);
    setShowDropdown(false);
    if (showForm) setShowForm(false);
    if (editingExpense) setEditingExpense(null);
  };

  // Handle Excel upload click - NEW FUNCTION
  const handleExcelUploadClick = () => {
    setShowExpenseUpload(true);
    setShowDropdown(false);
    if (showForm) setShowForm(false);
    if (showScanner) setShowScanner(false);
    if (editingExpense) setEditingExpense(null);
  };

  // Handle Excel upload completion - NEW FUNCTION
  const handleExpenseUploadComplete = () => {
    setShowExpenseUpload(false);
    setShowDropdown(false);
    fetchExpenses(); // Refresh the expenses list
  };

  // Get building ID for Excel upload - NEW FUNCTION
  const getBuildingId = () => {
    const userData = JSON.parse(localStorage.getItem('user'));
    return userData?.buildingId;
  };

  // Cleanup blob URLs on component unmount
  useEffect(() => {
    return () => {
      if (selectedInvoiceImage && selectedInvoiceImage.startsWith('blob:')) {
        URL.revokeObjectURL(selectedInvoiceImage);
      }
    };
  }, [selectedInvoiceImage]);

  // renderModal function removed - now using ExpenseForm component

  // Show ExpenseForm if form is active (similar to UserForm)
  if (showForm || editingExpense) {
    return (
      <ExpenseForm
        expense={editingExpense}
        newExpense={newExpense}
        isEditing={!!editingExpense}
        categories={categories}
        expenseTypes={expenseTypes}
        paymentMethods={paymentMethods}
        checks={checks}
        setChecks={setChecks}
        validationErrors={validationErrors}
        setValidationErrors={setValidationErrors}
        loading={loading}
        error={error}
        isUploading={isUploading}
        fileInputRef={fileInputRef}
        cameraInputRef={cameraInputRef}
        onExpenseInputChange={handleExpenseInputChange}
        onPaymentMethodChange={handlePaymentMethodChange}
        onFileUpload={handleFileUpload}
        onCameraCapture={handleCameraCapture}
        onRemoveUploadedFile={removeUploadedFile}
        onSave={editingExpense ? handleSaveEdit : handleAddExpense}
        onCancel={closeModal}
        formatFileSize={formatFileSize}
        onViewInvoice={viewInvoiceImage}
      />
    );
  }

  // Loading state
  if (loading && !showForm && !editingExpense) {
    return (
      <div className={styles.expensesManager}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>טוען הוצאות...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !showForm && !editingExpense) {
    return (
      <div className={styles.expensesManager}>
        <div className={styles.errorContainer}>
          <h2>שגיאה</h2>
          <p>{error}</p>
          <button onClick={fetchExpenses} className={styles.addButton}>
            נסה שוב
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.expensesManager}>
      <div className={styles.header}>
        <h1>ניהול הוצאות</h1>
        <div className={styles.stats}>
          <div className={styles.statCard}>
            <FileText size={24} className={styles.statIcon} />
            <div>
              <h3>סה"כ הוצאות</h3>
              <p className={styles.statValue}>{totalExpenses}</p>
            </div>
          </div>
          <div className={styles.statCard}>
            <DollarSign size={24} className={styles.statIcon} />
            <div>
              <h3>סכום כולל</h3>
              <p className={styles.statValue}>{totalAmount.toLocaleString()} ₪</p>
            </div>
          </div>
          <div className={styles.statCard}>
            <TrendingUp size={24} className={styles.statIcon} />
            <div>
              <h3>החודש</h3>
              <p className={styles.statValue}>{thisMonthExpenses}</p>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.actions}>
        <button
          className={styles.filterButton}
          onClick={() => setShowFilters(true)}
          title="סנן"
        >
          <Filter size={18} />
          סנן
          {(searchTerm || selectedMonth || selectedYear) && (
            <span className={styles.activeFilterBadge}></span>
          )}
        </button>


        <div className={styles.actionButtons}>
          {isDragEnabled && (
            <button
              onClick={toggleDragEnabled}
              className={styles.confirmDragButton}
              title="אישור שינויים"
            >
              <Check size={18} />
              <span>שמור</span>
            </button>
          )}

          <button
            className={styles.columnSettingsButton}
            onClick={() => setShowColumnSettings(true)}
            title="הגדרות עמודות"
            aria-label="הגדרות עמודות"
          >
            <Settings size={18} />
            עמודות
          </button>

          <div className={styles.actionButtonGroup}>
            <button className={styles.addButton} onClick={handleAddExpenseClick}>
              <Plus size={20} />
              הוסף הוצאה
            </button>

            <button
              className={styles.dropdownToggle}
              onClick={handleDropdownToggle}
              onTouchEnd={handleDropdownToggle} // Add touch support
              title="אפשרויות נוספות"
              type="button" // Explicitly set button type
            >
              <ChevronDown
                size={16}
                style={{
                  transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}
              />
            </button>

            {showDropdown && (
              <div className={styles.actionDropdownMenu}>
                <button
                  className={styles.actionDropdownItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleScannerClick();
                  }}
                  type="button"
                >
                  <Camera size={16} />
                  סריקת חשבונית
                </button>
                <button
                  className={styles.actionDropdownItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExcelUploadClick();
                  }}
                  type="button"
                >
                  <FileSpreadsheet size={16} />
                  העלאה מקובץ Excel/CSV
                </button>
                <button
                  className={styles.actionDropdownItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    showPopupManually();
                    setShowDropdown(false);
                  }}
                  type="button"
                >
                  <RotateCw size={16} />
                  הוצאות קבועות
                </button>
                <button
                  className={styles.actionDropdownItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    exportToExcel(false);
                    setShowDropdown(false);
                  }}
                  disabled={isExporting || currentExpenses.length === 0}
                  type="button"
                >
                  <FileSpreadsheet size={16} />
                  ייצא עמוד נוכחי ({currentExpenses.length})
                </button>
                <button
                  className={styles.actionDropdownItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    exportToExcel(true);
                    setShowDropdown(false);
                  }}
                  disabled={isExporting || filteredExpenses.length === 0}
                  type="button"
                >
                  <FileSpreadsheet size={16} />
                  ייצא כל הנתונים ({filteredExpenses.length})
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invoice Scanner */}
      {showScanner && (
        <InvoiceScanner onScanComplete={handleScanComplete} />
      )}

      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>טוען הוצאות...</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className={styles.tableContainer}>
            <table className={styles.expensesTable}>
              <thead>
                <tr>
                  {columnOrder.map(columnKey => {
                    if (!visibleColumns[columnKey]) return null;

                    const columnConfig = {
                      date: { label: 'תאריך', sortable: true },
                      description: { label: 'תיאור', sortable: true },
                      category: { label: 'קטגוריה', sortable: true },
                      expenseType: { label: 'סוג הוצאה', sortable: true },
                      paymentMethod: { label: 'אמצעי תשלום', sortable: true },
                      vendor: { label: 'ספק', sortable: true },
                      amount: { label: 'סכום (₪)', sortable: true },
                      invoice: { label: 'חשבונית', sortable: false },
                      actions: { label: 'פעולות', sortable: false }
                    };

                    const config = columnConfig[columnKey];
                    if (!config) return null;

                    return (
                      <th
                        key={columnKey}
                        onClick={config.sortable ? (e) => handleColumnHeaderClick(e, columnKey, config.label, config.sortable) : undefined}
                        className={config.sortable ? `${styles.sortableHeader} ${sortField === columnKey ? styles.activeSort : ''}` : ''}
                        style={config.sortable && !isDragEnabled ? { cursor: 'pointer', userSelect: 'none' } : {}}
                        draggable={isDragEnabled}
                        onDragStart={(e) => handleDragStart(e, columnKey)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, columnKey)}
                      >
                        {config.label}
                        {config.sortable && sortField === columnKey && (
                          <span className={styles.sortIndicator}>
                            {sortOrder.includes('desc') ? ' ↓' : ' ↑'}
                          </span>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {currentExpenses.length > 0 ? (
                  currentExpenses.map(expense => (
                    <tr key={expense.expenseID || expense.id}>
                      {columnOrder.map(columnKey => {
                        if (!visibleColumns[columnKey]) return null;

                        switch (columnKey) {
                          case 'date':
                            return <td key={columnKey}>{expense.date ? new Date(expense.date).toLocaleDateString('he-IL') : '-'}</td>;
                          case 'description':
                            return <td key={columnKey} className={styles.description}>{expense.description}</td>;
                          case 'category':
                            return <td key={columnKey}>{expense.category}</td>;
                          case 'expenseType':
                            return <td key={columnKey}>{expense.expenseType || '-'}</td>;
                          case 'paymentMethod':
                            return <td key={columnKey}>{expense.paymentMethod || '-'}</td>;
                          case 'vendor':
                            return <td key={columnKey}>{expense.vendor || '-'}</td>;
                          case 'amount':
                            return <td key={columnKey} className={styles.amount}>{parseFloat(expense.amount || 0).toLocaleString()} ₪</td>;
                          case 'invoice':
                            return (
                              <td key={columnKey}>
                                {expense.invoiceNumber && (
                                  <span style={{ fontSize: '12px', display: 'block' }}>
                                    #{expense.invoiceNumber}
                                  </span>
                                )}
                                {expense.invoiceImage ? (
                                  <button
                                    className={styles.invoiceButton}
                                    onClick={() => viewInvoiceImage(expense.invoiceImage)}
                                    title="הצג חשבונית"
                                  >
                                    <FileText size={16} />
                                  </button>
                                ) : expense.receiptImageURL ? (
                                  <button
                                    className={styles.invoiceButton}
                                    onClick={() => viewInvoiceImage(expense.receiptImageURL)}
                                    title="הצג קובץ מהשרת"
                                  >
                                    <FileText size={16} />
                                  </button>
                                ) : '-'}
                              </td>
                            );
                          case 'actions':
                            return (
                              <td key={columnKey} className={styles.actionsCell}>
                                <ActionMenu
                                  expense={expense}
                                  onEdit={handleEditMode}
                                  onDelete={handleDeleteExpense}
                                  onViewInvoice={viewInvoiceImage}
                                  loading={loading}
                                />
                              </td>
                            );
                          default:
                            return null;
                        }
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className={styles.noResults}>
                      {searchTerm || selectedCategory ? 'לא נמצאו הוצאות התואמות לחיפוש' : 'אין הוצאות'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards View */}
          <div className={styles.mobileCardsContainer}>
            <div className={styles.cardsGrid}>
              {currentExpenses.length > 0 ? (
                currentExpenses.map(expense => (
                  <MobileExpenseCard
                    key={expense.expenseID || expense.id}
                    expense={expense}
                    onEdit={() => handleEditMode(expense.expenseID)}
                    onDelete={() => handleDeleteExpense(expense.expenseID)}
                    onViewInvoice={viewInvoiceImage}
                  />
                ))
              ) : (
                <div className={styles.emptyState}>
                  {searchTerm || selectedCategory ? 'לא נמצאו הוצאות התואמות לחיפוש' : 'אין הוצאות'}
                </div>
              )}
            </div>
          </div>

          {filteredExpenses.length > 0 && (
            <div className={styles.pagination}>
              <div className={styles.paginationControls}>
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={styles.paginationButton}
                >
                  <ChevronRight size={20} />
                </button>
                <span className={styles.pageInfo}>
                  עמוד {currentPage} מתוך {totalPages}
                </span>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={styles.paginationButton}
                >
                  <ChevronLeft size={20} />
                </button>
              </div>
              <div className={styles.resultsPerPage}>
                <span>תוצאות בעמוד:</span>
                <select
                  value={expensesPerPage}
                  onChange={(e) => {
                    setExpensesPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
              </div>
              <div className={styles.totalInfo}>
                מציג {indexOfFirstExpense + 1}-{Math.min(indexOfLastExpense, filteredExpenses.length)} מתוך {filteredExpenses.length} הוצאות
              </div>
            </div>
          )}
        </>
      )}

      {/* Expense Excel Upload Modal - NEW */}
      {showExpenseUpload && (
        <ExpenseExcelUpload
          onClose={() => setShowExpenseUpload(false)}
          onExpensesCreated={handleExpenseUploadComplete}
          buildingId={getBuildingId()}
        />
      )}

      {/* Recurring Expenses Popup */}
      {showRecurringPopup && (
        <RecurringExpensesPopup
          expenses={recurringExpenses}
          isOpen={showRecurringPopup}
          onClose={closeRecurringPopup}
          onExpensesCreated={(createdExpenses) => {
            handleRecurringExpensesCreated(createdExpenses);
            fetchExpenses(); // Refresh the expenses list
          }}
          buildingId={buildingId}
        />
      )}

      {/* Invoice Image Modal */}
      {selectedInvoiceImage && (
        <div className={styles.invoiceModalOverlay} onClick={closeInvoiceImageModal}>
          <div className={styles.invoiceModalContent} onClick={e => e.stopPropagation()}>
            <button className={styles.modalCloseButton} onClick={closeInvoiceImageModal}>
              ×
            </button>
            <h3>תמונת חשבונית</h3>
            <div className={styles.modalImageContainer}>
              <img
                src={selectedInvoiceImage}
                alt="תמונת חשבונית"
                className={styles.modalInvoiceImage}
              />
            </div>
          </div>
        </div>
      )}

      {/* Column Settings Modal */}
      <ColumnSettingsModal
        isOpen={showColumnSettings}
        onClose={() => setShowColumnSettings(false)}
        visibleColumns={visibleColumns}
        toggleColumn={toggleColumn}
        resetColumns={resetColumns}
        isDragEnabled={isDragEnabled}
        toggleDragEnabled={toggleDragEnabled}
      />

      {/* Filters Modal */}
      {showFilters && createPortal(
        <div className={styles.modalOverlay} onClick={() => setShowFilters(false)}>
          <div className={`${styles.modalContent} ${styles.filtersModal}`} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                <Filter size={20} />
                סינון הוצאות
              </h3>
              <button
                onClick={() => setShowFilters(false)}
                className={styles.userModalCloseButton}
                aria-label="סגור"
              >
                <X size={20} />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>חיפוש</label>
                <div className={styles.searchBar}>
                  <input
                    type="text"
                    placeholder="חיפוש הוצאות..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search size={20} className={styles.searchIcon} />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>חודש</label>
                <div className={styles.dateFilter}>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className={styles.dateSelect}
                  >
                    <option value="">כל החודשים</option>
                    <option value="1">ינואר</option>
                    <option value="2">פברואר</option>
                    <option value="3">מרץ</option>
                    <option value="4">אפריל</option>
                    <option value="5">מאי</option>
                    <option value="6">יוני</option>
                    <option value="7">יולי</option>
                    <option value="8">אוגוסט</option>
                    <option value="9">ספטמבר</option>
                    <option value="10">אוקטובר</option>
                    <option value="11">נובמבר</option>
                    <option value="12">דצמבר</option>
                  </select>
                  <Calendar size={18} className={styles.filterIcon} />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>שנה</label>
                <div className={styles.dateFilter}>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className={styles.dateSelect}
                  >
                    <option value="">כל השנים</option>
                    {getAvailableYears.map(year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <Calendar size={18} className={styles.filterIcon} />
                </div>
              </div>
            </div>

            <div className={styles.modalActions}>
              {(searchTerm || selectedMonth || selectedYear) && (
                <button
                  onClick={clearFilters}
                  className={styles.resetButton}
                  title="נקה מסננים"
                >
                  <RotateCcw size={16} />
                  נקה הכל
                </button>
              )}
              <button
                onClick={() => setShowFilters(false)}
                className={styles.confirmButton}
              >
                <Filter size={16} />
                החל
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Success Animation */}
      {showSuccessAnimation && (
        <div className={styles.successOverlay}>
          <div className={styles.successCircle}>
            <svg className={styles.successCheckmark} viewBox="0 0 52 52">
              <path className={styles.checkmarkPath} fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
            </svg>
          </div>
        </div>
      )}

      {/* Mobile Search Button - Rendered outside main container */}
      {!showMobileSearch && createPortal(
        <button
          className={styles.mobileSearchButton}
          onClick={() => setShowMobileSearch(true)}
          aria-label="פתח חיפוש"
        >
          <Search size={22} />
          {(searchTerm || selectedMonth || selectedYear) && (
            <span className={styles.searchIndicatorDot}></span>
          )}
        </button>,
        document.body
      )}

      {/* Mobile Search Modal */}
      {showMobileSearch && createPortal(
        <div className={styles.mobileSearchModal}>
          <div className={styles.mobileSearchHeader}>
            <h3>חיפוש וסינון</h3>
            <button className={styles.mobileSearchClose} onClick={() => setShowMobileSearch(false)}>
              <X size={24} />
            </button>
          </div>
          <div className={styles.mobileSearchContent}>
            <div className={styles.mobileSearchField}>
              <Search size={20} className={styles.mobileSearchIcon} />
              <input
                type="text"
                placeholder="חיפוש הוצאות..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles.mobileSearchField}>
              <Calendar size={20} className={styles.mobileSearchIcon} />
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className={styles.mobileSearchSelect}
              >
                <option value="">כל החודשים</option>
                <option value="1">ינואר</option>
                <option value="2">פברואר</option>
                <option value="3">מרץ</option>
                <option value="4">אפריל</option>
                <option value="5">מאי</option>
                <option value="6">יוני</option>
                <option value="7">יולי</option>
                <option value="8">אוגוסט</option>
                <option value="9">ספטמבר</option>
                <option value="10">אוקטובר</option>
                <option value="11">נובמבר</option>
                <option value="12">דצמבר</option>
              </select>
            </div>
            <div className={styles.mobileSearchField}>
              <Calendar size={20} className={styles.mobileSearchIcon} />
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className={styles.mobileSearchSelect}
              >
                <option value="">כל השנים</option>
                {getAvailableYears.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.mobileSearchActions}>
              {(searchTerm || selectedMonth || selectedYear) && (
                <button
                  className={styles.mobileSearchClear}
                  onClick={clearFilters}
                >
                  <RotateCcw size={16} />
                  נקה הכל
                </button>
              )}
              <button className={styles.mobileSearchApply} onClick={() => setShowMobileSearch(false)}>
                <Filter size={16} />
                החל
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Sort Menu */}
      <SortMenu
        isOpen={sortMenuState.isOpen}
        columnKey={sortMenuState.columnKey}
        columnLabel={sortMenuState.columnLabel}
        position={sortMenuState.position}
        onClose={closeSortMenu}
        onSort={handleSort}
        currentSort={{ field: sortField, order: sortOrder.split('-')[1] }}
      />
    </div>
  );
};

export default Expenses;